import { useCallback, useEffect, useState } from 'react';

const reloadPage = () => window.location.reload();

const currentVersion = window.__APP_VERSION__;
const UpdateStatus = { checking: 0, current: 1, available: 2, major: 3, critical: 4 };

const resolveStatus = (data) => {
  if (data?.latestVersion === currentVersion) return UpdateStatus.current;

  const viableVersions = data?.viableVersions;
  const isRetiredBuild =
    Array.isArray(viableVersions) &&
    viableVersions.length > 0 &&
    !viableVersions.includes(currentVersion);

  if (data?.level === 'critical' || isRetiredBuild) return UpdateStatus.critical;
  if (data?.level === 'major') return UpdateStatus.major;
  return UpdateStatus.available;
};

export const useVersionCheck = ({ interval, type = 'manual', ignoreServerCache }) => {
  const [status, setStatus] = useState(UpdateStatus.checking);
  const [versions, setVersions] = useState({});

  const checkUpdate = useCallback(() => {
    if (typeof currentVersion === 'undefined') {
      setStatus(UpdateStatus.current);
      return;
    }

    setStatus(UpdateStatus.checking);

    let requestStr = `/${window.__APP_VERSION_FILE__}`;

    if (ignoreServerCache) {
      requestStr += `?v=${Date.now()}`;
    }

    fetch(requestStr)
      .then((res) => res.json())
      .then((data) => {
        setStatus(resolveStatus(data));
        setVersions(data);
      })
      .catch(() => {
        setStatus(UpdateStatus.current);
      });
  }, [ignoreServerCache]);

  useEffect(() => {
    if (type !== 'manual') {
      checkUpdate();
    }
  }, [checkUpdate, type]);

  useEffect(() => {
    if (type !== 'interval') {
      return;
    }

    // Keep polling after an update is announced, so a later critical release can still escalate.
    if (status === UpdateStatus.checking || status === UpdateStatus.critical) {
      return;
    }

    const timeoutId = window.setTimeout(() => checkUpdate(), interval || 10000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [type, interval, status, checkUpdate]);

  return { status, reloadPage, checkUpdate, UpdateStatus, currentVersion, versions };
};
